import { Component, inject, Input, OnInit } from '@angular/core';
import { createForm } from '@sonofabit/ng-core';
import { ContactPersonDetailsDto, UserItemDto } from '@artemis-software/wr-api';
import { BehaviorSubject } from 'rxjs';
import { NbDialogRef } from '@nebular/theme';

type ContactPersonInput = UserItemDto | ContactPersonDetailsDto;

const isUser = (value: ContactPersonInput | undefined | null): value is UserItemDto =>
  value != null && 'id' in value;

@Component({
  standalone: false,
  selector: 'wr-contact-person-dialog',
  templateUrl: './contact-person-dialog.component.html',
  styleUrls: ['./contact-person-dialog.component.scss'],
})
export class ContactPersonDialogComponent implements OnInit {
  readonly dialogRef = inject(NbDialogRef<ContactPersonDialogComponent>);

  @Input() contactPerson?: ContactPersonInput;
  @Input() tenantId?: string;

  selectedOption: 'user' | 'manual' = 'user';
  form?: ReturnType<typeof this.initForm>;
  readonly tenantIds$ = new BehaviorSubject<string[]>([]);

  ngOnInit(): void {
    this.form = this.initForm(this.contactPerson);
    if (this.contactPerson && !isUser(this.contactPerson)) {
      this.selectedOption = 'manual';
    }
    if (this.tenantId) {
      this.tenantIds$.next([this.tenantId]);
    }
  }

  private initForm(initialValue: ContactPersonInput | undefined) {
    const user = isUser(initialValue) ? initialValue : undefined;
    const phone = isUser(initialValue)
      ? initialValue.mobileNumber ?? initialValue.phoneNumber ?? ''
      : initialValue?.phone ?? '';
    return createForm(({ field }) => ({
      user: field({ value: user }),
      firstName: field({ value: initialValue?.firstName ?? '' }),
      lastName: field({ value: initialValue?.lastName ?? '' }),
      email: field({ value: initialValue?.email ?? '' }),
      phone: field({ value: phone }),
    }));
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.form) return;
    if (this.selectedOption === 'user') {
      const user = this.form.get('user')?.value;
      this.dialogRef.close(user ? { contactPerson: user } : undefined);
      return;
    }
    this.dialogRef.close({
      embeddedContactPerson: {
        firstName: this.form.get('firstName')?.value,
        lastName: this.form.get('lastName')?.value,
        email: this.form.get('email')?.value,
        phone: this.form.get('phone')?.value,
      },
    });
  }
}
